/**
 * BGP → Desativar/reativar em massa (#bgp/massa).
 *
 * Fluxo: escolher o transporte salvo (ex.: IX.SP) ou digitar rede/máscara (v4 e v6), ASN e caixas → buscar →
 * conferir a lista (tudo marcado, dá para desmarcar) → Desativar ou Reativar → motivo + "DESATIVAR N" → resultado
 * por caixa, com antes/depois e transcript. Escopo: borda Huawei com BGP. Sem reativação automática.
 * A permissão BGP_PEER_ADMIN é conferida na API.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError, type BgpAdminApplyResult, type BgpAdminPreview } from '../api';

const box = 'bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:ring-emerald-600';
const STATE_CLS: Record<string, string> = {
  ESTABLISHED: 'text-emerald-400 ring-emerald-700/40',
  ADMIN_DOWN: 'text-slate-400 ring-slate-700/40',
  IDLE: 'text-rose-400 ring-rose-700/40'
};
const key = (deviceId: number, ip: string) => `${deviceId}|${ip}`;

function errText(err: unknown): string {
  if (!(err instanceof HttpError)) return String((err as any)?.message ?? err);
  if (err.status === 403) return 'Sem permissão (BGP_PEER_ADMIN). Peça a um ADMIN para incluir seu grupo.';
  return err.detail?.detail ? `${err.message}: ${typeof err.detail.detail === 'string' ? err.detail.detail : JSON.stringify(err.detail.detail)}` : err.message;
}

type Peer = BgpAdminPreview['devices'][number]['peers'][number];
type SortCol = 'ip' | 'asn' | 'desc' | 'state' | 'pfx';
const STATE_ORDER: Record<string, number> = { ESTABLISHED: 0, ACTIVE: 1, CONNECT: 2, OPENSENT: 3, OPENCONFIRM: 4, IDLE: 5, ADMIN_DOWN: 6, UNKNOWN: 7 };
/** IP em chave ordenável: v4 antes de v6, cada octeto/hexteto com zero à esquerda. */
function ipKey(ip: string): string {
  if (ip.includes('.')) return '4' + ip.split('.').map((o) => o.padStart(3, '0')).join('.');
  const [h, t = ''] = ip.split('::');
  const a = h ? h.split(':') : [], b = t ? t.split(':') : [];
  const full = ip.includes('::') ? [...a, ...Array(8 - a.length - b.length).fill('0'), ...b] : a;
  return '6' + full.map((x) => x.padStart(4, '0')).join(':');
}
function sortVal(p: Peer, col: SortCol): string | number {
  switch (col) {
    case 'ip': return ipKey(p.peerIp);
    case 'asn': return p.peerAsn ?? 0;
    case 'desc': return (p.description ?? p.group ?? '~').toLowerCase();
    case 'state': return Math.min(...p.families.map((f) => STATE_ORDER[f.state] ?? 9));
    case 'pfx': return Math.max(...p.families.map((f) => f.receivedPfx ?? -1));
  }
}

function splitList(s: string): string[] {
  return s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
}

/** Só grupo BGP (permission BGP_PEER_ADMIN) ou ADMIN; a API confere de novo em todas as rotas. */
export function BgpBulkPage() {
  const meQ = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false, staleTime: 60_000 });
  if (!meQ.data) return <div className="text-xs text-slate-500">carregando…</div>;
  if (meQ.data.user.role !== 'ADMIN' && !meQ.data.permissions?.includes('BGP_PEER_ADMIN')) {
    return (
      <section className="space-y-3">
        <button onClick={() => { window.location.hash = '#bgp'; }} className="text-xs text-slate-400 hover:text-emerald-400">← voltar</button>
        <div className="rounded-lg ring-1 ring-amber-700/40 bg-amber-950/20 p-3 text-xs text-amber-200">
          Desativar/reativar peers BGP em massa é restrito ao grupo <b>BGP</b>. Peça a um ADMIN para incluir seu usuário.
        </div>
      </section>
    );
  }
  return <BgpBulkInner />;
}

function BgpBulkInner() {
  const qc = useQueryClient();
  const trQ = useQuery({ queryKey: ['bgp-transports'], queryFn: api.bgpTransports });
  const ovQ = useQuery({ queryKey: ['bgp-overview'], queryFn: api.bgpOverview });
  const [transportId, setTransportId] = useState<number | ''>('');
  const [cidrText, setCidrText] = useState('');
  const [asnText, setAsnText] = useState('');
  const [deviceIds, setDeviceIds] = useState<number[]>([]);
  const [preview, setPreview] = useState<BgpAdminPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<'disable' | 'enable' | null>(null);
  const [saveName, setSaveName] = useState('');

  const search = useMutation({
    mutationFn: () => api.bgpAdminPreview({
      cidrs: splitList(cidrText),
      transportId: transportId === '' ? undefined : transportId,
      asns: splitList(asnText).map(Number).filter((n) => Number.isInteger(n) && n > 0),
      deviceIds
    }),
    onSuccess: (p) => {
      setPreview(p);
      setSelected(new Set(p.devices.flatMap((d) => d.peers.map((x) => key(d.id, x.peerIp)))));
    }
  });
  const saveTr = useMutation({
    mutationFn: () => api.bgpTransportSave({ name: saveName.trim(), cidrs: splitList(cidrText) }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['bgp-transports'] }); setTransportId(r.transport.id); setSaveName(''); }
  });
  const delTr = useMutation({
    mutationFn: (id: number) => api.bgpTransportDelete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bgp-transports'] }); setTransportId(''); }
  });

  // Filtro (IP, descrição, grupo ou AS) e ordenação por coluna. Peer escondido pelo filtro NÃO entra no lote,
  // mesmo marcado: o que se vê é o que se aplica.
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ col: SortCol; dir: 1 | -1 }>({ col: 'asn', dir: 1 });
  const visible = useMemo(() => {
    const t = q.trim().toLowerCase();
    const m = new Map<number, Peer[]>();
    for (const d of preview?.devices ?? []) {
      const rows = d.peers.filter((p) => !t || [p.peerIp, p.description, p.group, p.peerAsn?.toString()].some((v) => v?.toLowerCase().includes(t)));
      rows.sort((a, b) => {
        const x = sortVal(a, sort.col), y = sortVal(b, sort.col);
        return (x < y ? -1 : x > y ? 1 : ipKey(a.peerIp).localeCompare(ipKey(b.peerIp))) * sort.dir;
      });
      m.set(d.id, rows);
    }
    return m;
  }, [preview, q, sort]);
  const items = useMemo(() => (preview?.devices ?? []).flatMap((d) =>
    (visible.get(d.id) ?? []).filter((p) => selected.has(key(d.id, p.peerIp))).map((p) => ({ deviceId: d.id, peerIp: p.peerIp, adminDown: p.adminDown }))), [preview, selected, visible]);
  const inLote = new Set(items.map((i) => key(i.deviceId, i.peerIp)));
  // aviso local (a API recusa de qualquer jeito): o lote leva todo o trânsito de alguma caixa?
  const isolated = (preview?.devices ?? []).filter((d) => d.transit.length > 0 && d.transit.every((ip) => inLote.has(key(d.id, ip))));
  const th = (col: SortCol, label: string) => (
    <th className="text-left px-2 py-1 cursor-pointer select-none hover:text-slate-200"
      onClick={() => setSort((s) => (s.col === col ? { col, dir: (s.dir * -1) as 1 | -1 } : { col, dir: 1 }))}>
      {label}{sort.col === col ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const transport = trQ.data?.transports.find((t) => t.id === transportId);
  const canSearch = splitList(cidrText).length > 0 || transportId !== '' || splitList(asnText).length > 0;

  const toggle = (k: string) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleDevice = (d: BgpAdminPreview['devices'][number], on: boolean) => setSelected((s) => {
    const n = new Set(s);
    (visible.get(d.id) ?? []).forEach((p) => (on ? n.add(key(d.id, p.peerIp)) : n.delete(key(d.id, p.peerIp))));
    return n;
  });

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => { window.location.hash = '#bgp'; }} className="text-xs text-slate-400 hover:text-emerald-400">← voltar</button>
        <h1 className="text-xl font-semibold">BGP — desativar/reativar peers em massa</h1>
      </div>

      <div className="rounded-lg ring-1 ring-slate-800 bg-slate-900/50 p-3 space-y-3 text-xs">
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block text-slate-400">
            Transporte salvo
            <div className="flex gap-2 mt-1">
              <select value={transportId} onChange={(e) => setTransportId(e.target.value === '' ? '' : Number(e.target.value))} className={`${box} flex-1`}>
                <option value="">—</option>
                {trQ.data?.transports.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {transport && (
                <button onClick={() => { if (confirm(`Remover o transporte salvo "${transport.name}"? (não mexe em nenhum peer)`)) delTr.mutate(transport.id); }}
                  className="px-2 rounded ring-1 ring-slate-700 text-slate-400 hover:text-rose-300">remover</button>
              )}
            </div>
            {transport && <div className="mt-1 font-mono text-[10px] text-slate-500">{transport.cidrs.join('  ')}</div>}
          </label>
          <label className="block text-slate-400">
            Rede/máscara (v4 e v6, uma por linha)
            <textarea value={cidrText} onChange={(e) => setCidrText(e.target.value)} rows={3} placeholder={'187.16.208.0/20\n2001:12f8::/64'}
              className={`${box} mt-1 w-full font-mono`} />
          </label>
          <div className="space-y-2">
            <label className="block text-slate-400">
              ASN (opcional, separado por vírgula)
              <input value={asnText} onChange={(e) => setAsnText(e.target.value)} placeholder="26162, 16509" className={`${box} mt-1 w-full font-mono`} />
            </label>
            <div className="text-slate-400">
              Caixas (nenhuma marcada = todas)
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {ovQ.data?.devices.map((d) => (
                  <label key={d.id} className="flex items-center gap-1 text-slate-300">
                    <input type="checkbox" checked={deviceIds.includes(d.id)}
                      onChange={(e) => setDeviceIds((v) => (e.target.checked ? [...v, d.id] : v.filter((x) => x !== d.id)))} />
                    <span className="font-mono text-[10px]">{d.hostname}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => search.mutate()} disabled={!canSearch || search.isPending}
            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-40">
            {search.isPending ? 'consultando as caixas…' : 'Buscar peers (ao vivo)'}
          </button>
          {splitList(cidrText).length > 0 && (
            <>
              <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="nome (ex.: IX.SP)" className={`${box} w-40`} />
              <button onClick={() => saveTr.mutate()} disabled={saveName.trim().length < 2 || saveTr.isPending}
                className="px-3 py-1 rounded ring-1 ring-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40">salvar como transporte</button>
            </>
          )}
          {(search.error || saveTr.error || delTr.error) && <span className="text-rose-300">{errText(search.error ?? saveTr.error ?? delTr.error)}</span>}
        </div>
      </div>

      {preview && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="text-slate-300">{preview.total} peers encontrados · <b>{items.length}</b> marcados</span>
            <span className="text-slate-500 font-mono text-[10px]">{preview.cidrs.join('  ')}</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="filtrar por IP, descrição, grupo ou AS"
              className={`${box} w-64`} />
            {q && <span className="text-amber-300 text-[10px]">só os peers visíveis entram no lote</span>}
            <div className="flex-1" />
            <button onClick={() => setAction('disable')} disabled={!items.length || isolated.length > 0}
              className="px-3 py-1 rounded bg-rose-700 hover:bg-rose-600 text-white disabled:opacity-40">Desativar {items.length}</button>
            <button onClick={() => setAction('enable')} disabled={!items.length}
              className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-40">Reativar {items.length}</button>
          </div>
          {isolated.length > 0 && (
            <div className="text-xs text-rose-300 ring-1 ring-rose-700/50 bg-rose-900/20 rounded p-2">
              A seleção desliga todo o trânsito (peers de tabela cheia) de {isolated.map((d) => d.hostname).join(', ')}. Desmarque ao menos um para desativar.
            </div>
          )}
          {preview.devices.length === 0 && <div className="text-xs text-slate-500">Nenhum peer nesse filtro.</div>}
          {preview.devices.map((d) => {
            const rows = visible.get(d.id) ?? [];
            if (!rows.length && !d.error) return null;
            const marked = rows.filter((p) => selected.has(key(d.id, p.peerIp))).length;
            return (
              <div key={d.id} className="rounded-lg ring-1 ring-slate-800 bg-slate-900/50">
                <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-800 text-xs">
                  <input type="checkbox" checked={rows.length > 0 && marked === rows.length} onChange={(e) => toggleDevice(d, e.target.checked)} />
                  <span className="font-mono text-slate-200">{d.hostname}</span>
                  <span className="text-slate-500">{marked}/{rows.length} marcados{rows.length !== d.peers.length ? ` (de ${d.peers.length})` : ''} · {rows.filter((p) => p.adminDown).length} já desativados</span>
                  {d.source === 'live'
                    ? <span className="ml-auto text-[10px] text-emerald-400">ao vivo · {d.checkedAt ? new Date(d.checkedAt).toLocaleTimeString('pt-BR') : ''}</span>
                    : <span className="ml-auto text-[10px] text-amber-300" title={d.error}>do último poll{d.checkedAt ? ` (${new Date(d.checkedAt).toLocaleTimeString('pt-BR')})` : ''}: consulta ao vivo falhou — {d.error}</span>}
                </div>
                <table className="w-full text-xs">
                  <thead className="text-slate-400"><tr className="border-b border-slate-800">
                    <th className="w-8" />{th('ip', 'Peer')}{th('asn', 'AS')}{th('desc', 'Descrição / grupo')}
                    {th('state', 'Estado (família)')}{th('pfx', 'Prefixos recebidos')}
                  </tr></thead>
                  <tbody>
                    {rows.map((p) => {
                      const k = key(d.id, p.peerIp);
                      return (
                        <tr key={k} className="border-b border-slate-800/60 hover:bg-slate-800/30 cursor-pointer" onClick={() => toggle(k)}>
                          <td className="text-center"><input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} onClick={(e) => e.stopPropagation()} /></td>
                          <td className="px-2 py-1 font-mono text-slate-200">{p.peerIp}{d.transit.includes(p.peerIp) && <span className="ml-2 text-[10px] text-amber-300">trânsito</span>}</td>
                          <td className="px-2 py-1">{p.peerAsn ?? '—'}</td>
                          <td className="px-2 py-1 text-slate-300">
                            {p.description ?? <span className="text-slate-600">—</span>}
                            {p.group && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] ring-1 ring-sky-700/40 text-sky-300">{p.group}</span>}
                          </td>
                          <td className="px-2 py-1 space-x-1">
                            {p.families.map((f) => (
                              <span key={f.family} className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${STATE_CLS[f.state] ?? 'text-amber-400 ring-amber-700/40'}`}>
                                {f.state} <span className="text-slate-500">{f.family === 'IPV6_UNICAST' ? 'v6' : 'v4'}</span>
                              </span>
                            ))}
                          </td>
                          <td className="px-2 py-1 text-slate-400">{p.families.map((f) => f.receivedPfx ?? '—').join(' / ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {action && (
        <BgpBulkModal enabled={action === 'enable'} items={items.map(({ deviceId, peerIp }) => ({ deviceId, peerIp }))}
          devices={preview?.devices.filter((d) => items.some((i) => i.deviceId === d.id)).map((d) => d.hostname) ?? []}
          onClose={(done) => { setAction(null); if (done) search.mutate(); }} />
      )}
    </section>
  );
}

function BgpBulkModal({ enabled, items, devices, onClose }: {
  enabled: boolean; items: { deviceId: number; peerIp: string }[]; devices: string[]; onClose: (done: boolean) => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const expected = `${enabled ? 'REATIVAR' : 'DESATIVAR'} ${items.length}`;
  const m = useMutation<BgpAdminApplyResult>({
    mutationFn: () => api.bgpAdminApply({ enabled, reason: reason.trim(), confirm: confirmText.trim(), items }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bgp-overview'] }); qc.invalidateQueries({ queryKey: ['bgp-peers'] }); }
  });
  const ready = reason.trim().length >= 5 && confirmText.trim().toUpperCase() === expected && !m.isPending;
  const verb = enabled ? 'Reativar' : 'Desativar';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { if (!m.isPending) onClose(!!m.data); }}>
      <div className="bg-slate-900 ring-1 ring-slate-700 rounded-lg w-full max-w-2xl p-4 space-y-3 text-sm max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="font-medium text-slate-100">{verb} {items.length} peers BGP</div>
        <div className="text-xs text-slate-400">em {devices.join(', ')}</div>
        <div className="text-xs rounded p-2 ring-1 text-slate-300 ring-slate-700 bg-slate-950">
          {enabled ? '`undo peer <ip> ignore`' : '`peer <ip> ignore`'}: um commit por caixa, sem save (um reboot devolve o estado salvo).
          Vale para o peer inteiro (v4 e v6 do mesmo IP). Não há reativação automática.
        </div>

        {!m.data && (
          <>
            <label className="block text-xs text-slate-400">
              Motivo (vai para a auditoria)
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className={`${box} mt-1 w-full`} />
            </label>
            <label className="block text-xs text-slate-400">
              Digite <span className="font-mono text-slate-300">{expected}</span> para confirmar
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className={`${box} mt-1 w-full font-mono`} />
            </label>
          </>
        )}

        {m.error && <div className="text-xs text-rose-300 ring-1 ring-rose-700/50 bg-rose-900/20 rounded p-2 whitespace-pre-wrap">{errText(m.error)}</div>}

        {m.data && (
          <div className="space-y-3">
            {m.data.results.map((r) => {
              const changed = r.peers.filter((p) => p.changed).length;
              const skipped = r.peers.filter((p) => p.skipped).length;
              return (
                <div key={r.deviceId} className="space-y-1">
                  <div className={`text-xs ${r.ok ? 'text-slate-200' : 'text-rose-300'}`}>
                    <span className="font-mono">{r.hostname}</span>:{' '}
                    {r.ok ? <>{changed} alterados{skipped > 0 && `, ${skipped} já estavam assim`}{r.peers.length - changed - skipped > 0 && <span className="text-amber-300">, {r.peers.length - changed - skipped} sem confirmação</span>}</> : r.error}
                  </div>
                  {r.ok && (
                    <div className="text-[10px] text-slate-400 font-mono">
                      {r.peers.filter((p) => !p.skipped).map((p) => <div key={p.peerIp}>{p.peerIp}: {p.before ?? '—'} → {p.after ?? '—'}</div>)}
                    </div>
                  )}
                  {r.transcript && (
                    <details><summary className="text-[10px] text-slate-500 cursor-pointer">transcript</summary>
                      <pre className="text-[10px] text-slate-400 bg-slate-950 ring-1 ring-slate-800 rounded p-2 max-h-60 overflow-auto whitespace-pre-wrap">{r.transcript}</pre>
                    </details>
                  )}
                </div>
              );
            })}
            {enabled && <div className="text-[10px] text-slate-500">Logo depois do undo ignore o peer passa por Idle/Connect/Active até fechar a sessão; a tela atualiza no próximo poll (30 s).</div>}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={() => onClose(!!m.data)} disabled={m.isPending}
            className="px-3 py-1 rounded text-xs ring-1 ring-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">{m.data ? 'Fechar' : 'Cancelar'}</button>
          {!m.data && (
            <button onClick={() => m.mutate()} disabled={!ready}
              className={`px-3 py-1 rounded text-xs text-white disabled:opacity-40 ${enabled ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-rose-700 hover:bg-rose-600'}`}>
              {m.isPending ? 'aplicando…' : verb}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
